import React, { useEffect, useMemo, useState } from 'react';
import {
  Activity, BrainCircuit, Check, Database, Eye, FileJson2, Gauge, KeyRound, Languages,
  LoaderCircle, Network, Pencil, Plus, RefreshCw, Save, Search, ServerCog, ShieldCheck, Sparkles,
  TimerReset, Trash2, WandSparkles, Workflow,
} from 'lucide-react';
import { api } from '../api.js';
import {
  Badge, Button, Callout, IconButton, Modal, PageHeader, SectionHeader,
} from '../components/ui.jsx';

const ROLE_CONFIG = [
  { key: 'vision', label: '简历 OCR 模型', description: '非文本 PDF 与低覆盖页面视觉复读', icon: Eye, needsVision: true },
  { key: 'scoring', label: '审核模型', description: '候选人证据综合与岗位评分', icon: BrainCircuit },
  { key: 'company_research', label: '公司背景核验模型', description: '核验履历公司背景并保存来源；配置搜索服务后只负责归纳检索结果', icon: Search, needsWebSearch: true },
  { key: 'translation', label: '翻译模型', description: '独立分片翻译简历原文', icon: Languages },
  { key: 'fallback', label: 'Fallback 模型', description: '主 Provider 不可用时接管文本任务', icon: RefreshCw },
];

const EMPTY_MODEL = {
  modelId: '', name: '', inputModalities: ['text'], structuredMode: 'json_schema', streamingMode: 'stream', webSearchMode: 'none', enabled: true,
};

function NumberStepper({ value, onChange, min, max, step = 1, suffix }) {
  const next = (delta) => Math.max(min, Math.min(max, Number(value || 0) + delta));
  return (
    <div className="number-stepper">
      <button type="button" aria-label="减少" onClick={() => onChange(next(-step))}>-</button>
      <input type="number" min={min} max={max} step={step} value={value} onChange={(event) => onChange(Math.max(min, Math.min(max, Number(event.target.value))))} />
      <span>{suffix}</span>
      <button type="button" aria-label="增加" onClick={() => onChange(next(step))}>+</button>
    </div>
  );
}

function ProviderMark({ type }) {
  const Icon = type === 'openrouter' ? Network : type === 'deepseek' ? Sparkles : ServerCog;
  return <span className={`provider-logo provider-${type}`}><Icon size={19} /></span>;
}

function CapabilitySourceBadge({ source, catalogSource }) {
  if (catalogSource === 'preset') return <Badge tone="neutral">系统预设</Badge>;
  if (source === 'provider') return <Badge tone="success">上游声明</Badge>;
  if (source === 'inferred') return <Badge tone="info">型号识别</Badge>;
  if (source === 'default') return <Badge tone="warning">上游未声明</Badge>;
  return <Badge tone="neutral">能力人工配置</Badge>;
}

function ModelFields({ value, onChange }) {
  const update = (key, next) => onChange({ ...value, [key]: next });
  const toggleModality = (modality) => {
    const current = new Set(value.inputModalities || ['text']);
    if (current.has(modality)) current.delete(modality); else current.add(modality);
    current.add('text');
    update('inputModalities', [...current]);
  };
  return (
    <div className="model-editor-fields">
      <label><span>Model ID</span><input value={value.modelId} onChange={(event) => update('modelId', event.target.value)} placeholder="provider/model-name" /></label>
      <label><span>显示名称</span><input value={value.name} onChange={(event) => update('name', event.target.value)} placeholder="Model name" /></label>
      <label><span>结构化输出</span><select value={value.structuredMode} onChange={(event) => update('structuredMode', event.target.value)}><option value="json_schema">JSON Schema</option><option value="json_object">JSON Object</option><option value="prompt">Prompt JSON</option></select></label>
      <label><span>响应方式</span><select value={value.streamingMode || 'stream'} onChange={(event) => update('streamingMode', event.target.value)}><option value="stream">流式</option><option value="non_stream">非流式</option></select></label>
      <label><span>联网搜索协议</span><select value={value.webSearchMode || 'none'} onChange={(event) => update('webSearchMode', event.target.value)}><option value="none">不启用</option><option value="xai_responses">xAI Responses · web_search</option><option value="glm_chat">GLM Chat · web_search</option></select></label>
      <div className="check-row"><label><input type="checkbox" checked={(value.inputModalities || []).includes('image')} onChange={() => toggleModality('image')} />支持视觉输入</label><label><input type="checkbox" checked={value.enabled !== false} onChange={(event) => update('enabled', event.target.checked)} />启用模型</label></div>
    </div>
  );
}

export default function ModelsPage({ state, refresh, notify }) {
  const settings = state?.settings || {};
  const providers = settings.providers || [];
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [providerForm, setProviderForm] = useState({ name: '', baseUrl: '', enabled: true });
  const [providerKey, setProviderKey] = useState('');
  const [testModelId, setTestModelId] = useState('');
  const [roles, setRoles] = useState({});
  const [processing, setProcessing] = useState({});
  const [contextSettings, setContextSettings] = useState({});
  const [langfuse, setLangfuse] = useState({});
  const [langfuseSecret, setLangfuseSecret] = useState('');
  const [search, setSearch] = useState({});
  const [searchKey, setSearchKey] = useState('');
  const [searchDirty, setSearchDirty] = useState(false);
  const [searchFeedback, setSearchFeedback] = useState(null);
  const [providerModal, setProviderModal] = useState(false);
  const [newProvider, setNewProvider] = useState({ name: '', baseUrl: '', apiKey: '', model: { ...EMPTY_MODEL } });
  const [modelModal, setModelModal] = useState(null);
  const [modelDraft, setModelDraft] = useState({ ...EMPTY_MODEL });
  const [modelSearch, setModelSearch] = useState('');
  const [busy, setBusy] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [providerFeedback, setProviderFeedback] = useState(null);
  const [langfuseFeedback, setLangfuseFeedback] = useState(null);
  const [runtimeDirty, setRuntimeDirty] = useState(false);
  const [langfuseDirty, setLangfuseDirty] = useState(false);

  const selectedProvider = providers.find((provider) => provider.id === selectedProviderId) || providers[0] || null;

  useEffect(() => {
    const nextId = providers.some((provider) => provider.id === selectedProviderId)
      ? selectedProviderId : providers[0]?.id || '';
    setSelectedProviderId(nextId);
    if (!runtimeDirty) {
      setRoles(settings.roles || {});
      setProcessing(settings.processing || {});
      setContextSettings(settings.context || {});
    }
    if (!langfuseDirty) setLangfuse(settings.langfuse || {});
    if (!searchDirty) setSearch(settings.search || {});
  }, [settings, runtimeDirty, langfuseDirty, searchDirty]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedProvider) return;
    setProviderForm({ name: selectedProvider.name, baseUrl: selectedProvider.baseUrl, enabled: selectedProvider.enabled });
    setProviderKey('');
    setTestResult(null);
    setModelSearch('');
    setTestModelId(selectedProvider.models.find((model) => model.enabled)?.id || '');
  }, [selectedProviderId, selectedProvider?.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setProviderFeedback(null);
  }, [selectedProviderId]);

  const filteredModels = useMemo(() => {
    const query = modelSearch.trim().toLowerCase();
    if (!query) return selectedProvider?.models || [];
    return (selectedProvider?.models || []).filter((model) =>
      `${model.name} ${model.modelId}`.toLowerCase().includes(query)
    );
  }, [selectedProvider, modelSearch]);

  const runAction = async (key, action, success) => {
    setBusy(key);
    try {
      await action();
      if (success) notify(success);
      await refresh();
    } catch (error) {
      notify(error.message, 'danger');
      throw error;
    } finally {
      setBusy('');
    }
  };

  const updateProviderForm = (patch) => {
    setProviderForm((current) => ({ ...current, ...patch }));
    setProviderFeedback(null);
  };

  const saveProvider = async () => {
    if (!selectedProvider) return;
    setBusy('provider-save');
    setProviderFeedback(null);
    try {
      await api.updateProvider(selectedProvider.id, {
        ...providerForm,
        apiKey: providerKey.trim() || undefined,
      });
      const keyUpdated = Boolean(providerKey.trim());
      setProviderKey('');
      await refresh();
      setProviderFeedback({
        tone: 'success',
        title: 'Provider 配置已保存',
        message: `${providerForm.name || selectedProvider.name} · ${new Date().toLocaleTimeString('zh-CN')}${keyUpdated ? ' · API Key 已更新' : ''}`,
      });
      notify('Provider 已保存');
    } catch (error) {
      setProviderFeedback({ tone: 'danger', title: 'Provider 保存失败', message: error.message });
      notify(error.message, 'danger');
    } finally {
      setBusy('');
    }
  };

  const testProvider = async () => {
    setBusy('provider-test'); setTestResult(null);
    try {
      const result = await api.testProvider(selectedProvider.id, {
        providerModelId: testModelId,
        apiKey: providerKey || undefined,
        baseUrl: providerForm.baseUrl,
      });
      setTestResult({ ok: true, ...result });
      notify('Provider 连接与结构化输出验证通过');
    } catch (error) {
      setTestResult({ ok: false, message: error.message, code: error.code });
      notify(error.message, 'danger');
    } finally { setBusy(''); }
  };

  const createProvider = () => runAction('provider-create', () => api.createProvider(newProvider), 'Custom Provider 已创建')
    .then(() => { setProviderModal(false); setNewProvider({ name: '', baseUrl: '', apiKey: '', model: { ...EMPTY_MODEL } }); })
    .catch(() => {});

  const saveModel = () => {
    const action = modelModal?.model
      ? () => api.updateProviderModel(selectedProvider.id, modelModal.model.id, modelDraft)
      : () => api.addProviderModel(selectedProvider.id, modelDraft);
    return runAction('model-save', action, modelModal?.model ? '模型已更新' : '模型已添加')
      .then(() => setModelModal(null)).catch(() => {});
  };

  const saveRuntime = async () => {
    setBusy('runtime-save');
    try {
      await api.saveSettings({
        roles: Object.fromEntries(ROLE_CONFIG.map(({ key }) => [key, {
          providerId: roles[key]?.providerId || null,
          providerModelId: roles[key]?.providerModelId || null,
        }])),
        processing,
        context: contextSettings,
      });
      notify('模型分工与处理策略已保存');
      await refresh();
      setRuntimeDirty(false);
    } catch (error) { notify(error.message, 'danger'); }
    finally { setBusy(''); }
  };

  const updateRoleProvider = (role, providerId) => {
    const provider = providers.find((item) => item.id === providerId);
    const firstModel = provider?.models.find((model) => model.enabled);
    setRoles((current) => ({ ...current, [role]: { providerId, providerModelId: firstModel?.id || null } }));
    setRuntimeDirty(true);
  };

  const updateSearch = (patch) => {
    setSearch((current) => ({ ...current, ...patch }));
    setSearchDirty(true);
    setSearchFeedback(null);
  };

  const saveSearch = async () => {
    setBusy('search-save');
    setSearchFeedback(null);
    try {
      const saved = await api.saveSearchSettings({
        enabled: Boolean(search.enabled),
        provider: search.provider || 'parallel',
        baseUrl: search.baseUrl || '',
        mode: search.mode || 'advanced',
        maxResults: Number(search.maxResults) || 8,
        maxCharsPerResult: Number(search.maxCharsPerResult) || 1500,
        ...(searchKey.trim() ? { apiKey: searchKey.trim() } : {}),
      });
      setSearch(saved);
      setSearchKey('');
      setSearchDirty(false);
      setSearchFeedback({ tone: 'success', title: '搜索服务配置已保存', message: saved.configured ? '公司背景核验将改用该服务检索。' : '仍需启用并填写 API Key 后才会生效。' });
      notify('搜索服务配置已保存');
    } catch (error) {
      setSearchFeedback({ tone: 'danger', title: '搜索服务保存失败', message: error.message });
    } finally {
      setBusy(null);
    }
  };

  const testSearch = async () => {
    setBusy('search-test');
    setSearchFeedback(null);
    try {
      const checked = await api.testSearchProvider();
      setSearch(checked);
      setSearchFeedback({
        tone: checked.lastCheckStatus === 'ok' ? 'success' : 'danger',
        title: checked.lastCheckStatus === 'ok' ? '搜索服务可用' : '搜索服务不可用',
        message: checked.lastCheckMessage || '',
      });
    } catch (error) {
      setSearchFeedback({ tone: 'danger', title: '搜索服务检查失败', message: error.message });
    } finally {
      setBusy(null);
    }
  };

  const updateLangfuse = (patch) => {
    setLangfuse((current) => ({ ...current, ...patch }));
    setLangfuseDirty(true);
    setLangfuseFeedback(null);
  };

  const currentLangfusePayload = () => ({
    enabled: Boolean(langfuse.enabled),
    baseUrl: langfuse.baseUrl || '',
    publicKey: langfuse.publicKey || '',
    environment: langfuse.environment || 'production',
    captureContent: Boolean(langfuse.captureContent),
    ...(langfuseSecret.trim() ? { secretKey: langfuseSecret.trim() } : {}),
  });

  const saveLangfuse = async () => {
    setBusy('langfuse-save');
    setLangfuseFeedback(null);
    try {
      const saved = await api.saveLangfuse(currentLangfusePayload());
      setLangfuse(saved);
      setLangfuseSecret('');
      await refresh();
      setLangfuseDirty(false);
      setLangfuseFeedback({
        tone: 'success',
        title: 'Langfuse 配置已保存',
        message: `${saved.baseUrl} · ${saved.environment} · ${saved.hasSecretKey ? 'Secret Key 已配置' : 'Secret Key 未配置'}`,
      });
      notify('Langfuse 配置已保存');
    } catch (error) {
      setLangfuseFeedback({ tone: 'danger', title: 'Langfuse 保存失败', message: error.message });
      notify(error.message, 'danger');
    } finally {
      setBusy('');
    }
  };

  const testLangfuse = async () => {
    setBusy('langfuse-test');
    setLangfuseFeedback(null);
    try {
      const result = await api.testLangfuse(currentLangfusePayload());
      setLangfuseFeedback({
        tone: 'success',
        title: '测试 Trace 已发送',
        message: `Trace ID: ${result.traceId} · ${result.baseUrl} · ${result.environment}`,
      });
      notify('Langfuse 测试 Trace 已发送');
    } catch (error) {
      setLangfuseFeedback({ tone: 'danger', title: 'Langfuse 连接测试失败', message: error.message });
      notify(error.message, 'danger');
    } finally {
      setBusy('');
    }
  };

  const tools = state?.system?.extractionTools || {};
  const toolRows = [
    ['PDF 文本层', 'pdftotext'], ['PDF 页面渲染', 'pdftoppm'], ['传统 OCR', 'tesseract'], ['网页视觉采集', 'chrome'],
  ];

  return (
    <div className="page models-page">
      <PageHeader eyebrow="管理员设置" title="AI 运行设置" description="Provider、模型分工、上下文边界与运行追踪" actions={<Button variant="primary" icon={Save} loading={busy === 'runtime-save'} onClick={saveRuntime}>保存模型与策略</Button>} />

      <div className="models-layout">
        <div className="models-main">
          <section className="panel provider-section">
            <SectionHeader title="API Providers" description="Custom 为主要接入；DeepSeek 提供翻译与 Fallback；OpenRouter 保留为升级路径" action={<Button icon={Plus} size="sm" onClick={() => setProviderModal(true)}>新增 Custom</Button>} />
            <div className="provider-options">
              {providers.map((provider) => (
                <button key={provider.id} className={provider.id === selectedProvider?.id ? 'active' : ''} onClick={() => setSelectedProviderId(provider.id)}>
                  <ProviderMark type={provider.type} />
                  <div><strong>{provider.name}</strong><small>{provider.models.length} models · {provider.hasApiKey ? 'Key 已配置' : 'Key 未配置'}</small></div>
                  {provider.id === selectedProvider?.id ? <Check size={17} /> : <Badge tone={provider.enabled ? 'success' : 'neutral'}>{provider.enabled ? '启用' : '停用'}</Badge>}
                </button>
              ))}
            </div>

            {selectedProvider && <div className="provider-workspace">
              <div className="provider-fields provider-fields-wide">
                <label><span>Provider 名称</span><input value={providerForm.name} disabled={selectedProvider.isSystem} onChange={(event) => updateProviderForm({ name: event.target.value })} /></label>
                <label><span>API Base URL</span><div className="input-with-icon"><Network size={16} /><input value={providerForm.baseUrl} onChange={(event) => updateProviderForm({ baseUrl: event.target.value })} placeholder="https://api.example.com/v1" /></div></label>
                <label><span>API Key</span><div className="api-key-input"><KeyRound size={16} /><input type="password" autoComplete="new-password" value={providerKey} onChange={(event) => { setProviderKey(event.target.value); setProviderFeedback(null); }} placeholder={selectedProvider.hasApiKey ? `已保存 ${selectedProvider.apiKeyHint}` : '输入 API Key'} />{selectedProvider.hasApiKey && <Badge tone="success"><Check size={12} />已配置</Badge>}</div></label>
                <label className="provider-toggle"><span>Provider 状态</span><span><input type="checkbox" checked={providerForm.enabled} onChange={(event) => updateProviderForm({ enabled: event.target.checked })} />启用</span></label>
              </div>
              <div className="provider-actions">
                <Button icon={providerFeedback?.tone === 'success' ? Check : Save} loading={busy === 'provider-save'} onClick={saveProvider}>{providerFeedback?.tone === 'success' ? '已保存' : '保存 Provider'}</Button>
                <label className="test-model-select"><span>测试模型</span><select value={testModelId} onChange={(event) => setTestModelId(event.target.value)}>{selectedProvider.models.filter((model) => model.enabled).map((model) => <option key={model.id} value={model.id}>{model.name}</option>)}</select></label>
                <Button icon={WandSparkles} loading={busy === 'provider-test'} disabled={!testModelId} onClick={testProvider}>测试连接</Button>
                {selectedProvider.hasApiKey && <Button variant="ghost" icon={KeyRound} onClick={() => runAction('key-clear', () => api.updateProvider(selectedProvider.id, { ...providerForm, clearApiKey: true }), 'API Key 已移除').catch(() => {})}>移除 Key</Button>}
                {!selectedProvider.isSystem && <IconButton icon={Trash2} label="删除 Provider" variant="danger" onClick={() => {
                  if (window.confirm(`删除 ${selectedProvider.name}？`)) runAction('provider-delete', () => api.deleteProvider(selectedProvider.id), 'Provider 已删除').catch(() => {});
                }} />}
              </div>
              {providerFeedback && <Callout tone={providerFeedback.tone} title={providerFeedback.title}>{providerFeedback.message}</Callout>}
              {testResult && <Callout tone={testResult.ok ? 'success' : 'danger'} title={testResult.ok ? '连接验证通过' : '连接失败'}>{testResult.ok ? `${testResult.provider} · ${testResult.model} · ${testResult.structuredOutput}` : testResult.message}</Callout>}

              <div className="model-catalog-head">
                <div><h3>模型目录</h3><Badge tone="neutral">{selectedProvider.models.length}</Badge></div>
                <div><label className="search-field"><Network size={14} /><input value={modelSearch} onChange={(event) => setModelSearch(event.target.value)} placeholder="搜索 Model ID" /></label><IconButton icon={RefreshCw} label="从 Provider 更新模型" onClick={() => runAction('models-refresh', () => api.refreshProviderModels(selectedProvider.id), '模型目录已更新').catch(() => {})} disabled={!selectedProvider.hasApiKey || busy === 'models-refresh'} /><IconButton icon={Plus} label="添加模型" onClick={() => { setModelDraft({ ...EMPTY_MODEL, structuredMode: selectedProvider.type === 'deepseek' ? 'json_object' : 'json_schema' }); setModelModal({ model: null }); }} /></div>
              </div>
              <div className="provider-model-list">
                {filteredModels.map((model) => <div className="provider-model-row" key={model.id}>
                  <span className="model-source-icon"><FileJson2 size={16} /></span>
                  <div><strong>{model.name}</strong><code>{model.modelId}</code></div>
                  <div className="capability-tags">{model.inputModalities.includes('image') && <Badge tone="success">视觉</Badge>}{model.webSearchMode === 'xai_responses' && <Badge tone="success">xAI 搜索</Badge>}{model.webSearchMode === 'glm_chat' && <Badge tone="success">GLM 搜索</Badge>}<CapabilitySourceBadge source={model.capabilitySource} catalogSource={model.source} /><Badge tone="info">{model.structuredMode}</Badge><Badge tone="neutral">{model.streamingMode === 'non_stream' ? '非流式' : '流式'}</Badge>{!model.enabled && <Badge>停用</Badge>}</div>
                  <IconButton icon={Pencil} label="编辑模型" onClick={() => { setModelDraft({ ...model }); setModelModal({ model }); }} />
                  <IconButton icon={Trash2} label="删除模型" variant="danger" onClick={() => {
                    if (window.confirm(`删除模型 ${model.name}？`)) runAction('model-delete', () => api.deleteProviderModel(selectedProvider.id, model.id), '模型已删除').catch(() => {});
                  }} />
                </div>)}
                {!filteredModels.length && <div className="models-empty">没有匹配的模型</div>}
              </div>
            </div>}
          </section>

          <section className="panel model-roles-section">
            <SectionHeader title="模型分工" description="每项任务先固定 Provider，再选择该 Provider 下的模型" />
            <div className="model-role-list">
              {ROLE_CONFIG.map((role) => {
                const assignment = roles[role.key] || {};
                const provider = providers.find((item) => item.id === assignment.providerId);
                const models = provider?.models.filter((model) => model.enabled) || [];
                const selected = models.find((model) => model.id === assignment.providerModelId);
                const compatible = !role.needsVision || selected?.inputModalities?.includes('image');
                const searchServiceActive = role.needsWebSearch && Boolean(search.configured);
                const searchCompatible = !role.needsWebSearch || searchServiceActive || selected?.webSearchMode !== 'none';
                return <div className="model-role" key={role.key}>
                  <span className="model-role-icon"><role.icon size={19} /></span>
                  <div><strong>{role.label}</strong><small>{role.description}</small></div>
                  <label><span>Provider</span><select value={assignment.providerId || ''} onChange={(event) => updateRoleProvider(role.key, event.target.value)}>{providers.filter((item) => item.enabled).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
                  <label><span>Model</span><select value={assignment.providerModelId || ''} onChange={(event) => { setRoles((current) => ({ ...current, [role.key]: { ...assignment, providerModelId: event.target.value || null } })); setRuntimeDirty(true); }}><option value="">请选择模型</option>{models.map((model) => <option key={model.id} value={model.id}>{model.name}</option>)}</select></label>
                  <div className="capability-tags">{role.needsVision && <Badge tone={compatible ? 'success' : 'danger'}>{compatible ? '支持视觉' : '需要视觉模型'}</Badge>}{role.needsWebSearch && <Badge tone={searchCompatible ? 'success' : 'danger'}>{searchServiceActive ? '检索由搜索服务提供' : selected?.webSearchMode === 'xai_responses' ? 'xAI Web Search' : selected?.webSearchMode === 'glm_chat' ? 'GLM Web Search' : '需要搜索协议'}</Badge>}{selected && <CapabilitySourceBadge source={selected.capabilitySource} catalogSource={selected.source} />}{selected && <Badge tone="info">{selected.structuredMode}</Badge>}{selected && <Badge tone="neutral">{selected.streamingMode === 'non_stream' ? '非流式' : '流式'}</Badge>}</div>
                </div>;
              })}
            </div>
          </section>

          <section className="panel context-settings-section">
            <SectionHeader title="上下文策略" description="独立 Context Pack、来源清单、去重与分片" />
            <div className="context-status-row"><Badge tone="success"><Check size={12} />按功能隔离</Badge><Badge tone="success"><Check size={12} />保存 Context Manifest</Badge><Badge tone="success"><Check size={12} />证据去重</Badge><Badge tone="success"><Check size={12} />不共享审核结论</Badge></div>
            <div className="processing-number-grid context-number-grid">
              <div><span className="setting-icon"><BrainCircuit size={18} /></span><div><strong>审核上下文</strong><small>候选人证据 Token 上限</small></div><NumberStepper value={contextSettings.scoringTokenLimit || 18000} onChange={(value) => { setContextSettings((current) => ({ ...current, scoringTokenLimit: value })); setRuntimeDirty(true); }} min={4000} max={80000} step={1000} suffix="tokens" /></div>
              <div><span className="setting-icon"><BrainCircuit size={18} /></span><div><strong>审核输出预算</strong><small>推理模型的思考也计入此预算</small></div><NumberStepper value={contextSettings.scoringOutputTokenLimit || 48000} onChange={(value) => { setContextSettings((current) => ({ ...current, scoringOutputTokenLimit: value })); setRuntimeDirty(true); }} min={8000} max={120000} step={4000} suffix="tokens" /></div>
              <div><span className="setting-icon"><Languages size={18} /></span><div><strong>翻译分片</strong><small>每个独立翻译 Context</small></div><NumberStepper value={contextSettings.translationTokenLimit || 10000} onChange={(value) => { setContextSettings((current) => ({ ...current, translationTokenLimit: value })); setRuntimeDirty(true); }} min={2000} max={40000} step={1000} suffix="tokens" /></div>
            </div>
          </section>

          <section className="panel processing-settings-section">
            <SectionHeader title="处理策略" description="新任务的并发、重试与超时" />
            <div className="processing-number-grid">
              <div><span className="setting-icon"><Gauge size={18} /></span><div><strong>并发任务</strong><small>本机文件与模型调用</small></div><NumberStepper value={processing.concurrency || 2} onChange={(value) => { setProcessing((current) => ({ ...current, concurrency: value })); setRuntimeDirty(true); }} min={1} max={8} suffix="个" /></div>
              <div><span className="setting-icon"><RefreshCw size={18} /></span><div><strong>失败重试</strong><small>单个 Provider 内指数退避</small></div><NumberStepper value={processing.retryCount || 0} onChange={(value) => { setProcessing((current) => ({ ...current, retryCount: value })); setRuntimeDirty(true); }} min={0} max={6} suffix="次" /></div>
              <div><span className="setting-icon"><TimerReset size={18} /></span><div><strong>请求超时</strong><small>首字节、流式空闲、总时长分层；thinking 模型最低 300 秒</small></div><NumberStepper value={Math.round((processing.requestTimeoutMs || 90000) / 1000)} onChange={(value) => { setProcessing((current) => ({ ...current, requestTimeoutMs: value * 1000 })); setRuntimeDirty(true); }} min={5} max={1800} step={30} suffix="秒" /></div>
            </div>
          </section>

          <section className="panel langfuse-section">
            <SectionHeader
              title="搜索服务"
              description="公司背景核验的独立检索来源；不配置则回退到模型自带的联网搜索"
              action={<label className="switch-control"><input type="checkbox" checked={Boolean(search.enabled)} onChange={(event) => updateSearch({ enabled: event.target.checked })} /><span />{search.enabled ? '启用' : '停用'}</label>}
            />
            <div className="langfuse-fields">
              <label><span>服务商</span><select value={search.provider || 'parallel'} onChange={(event) => updateSearch({ provider: event.target.value })}><option value="parallel">Parallel</option></select></label>
              <label><span>Base URL</span><input value={search.baseUrl || ''} onChange={(event) => updateSearch({ baseUrl: event.target.value })} placeholder="https://api.parallel.ai" /></label>
              <label><span>检索模式</span><select value={search.mode || 'advanced'} onChange={(event) => updateSearch({ mode: event.target.value })}><option value="turbo">turbo</option><option value="fast">fast</option><option value="basic">basic</option><option value="advanced">advanced</option></select></label>
              <label><span>API Key</span><div className="api-key-input"><KeyRound size={16} /><input type="password" autoComplete="new-password" value={searchKey} onChange={(event) => { setSearchKey(event.target.value); setSearchDirty(true); setSearchFeedback(null); }} placeholder={search.hasApiKey ? `已保存 ${search.apiKeyHint}` : '搜索服务 API Key'} />{search.hasApiKey && <Badge tone="success">已配置</Badge>}</div></label>
            </div>
            <div className="processing-number-grid context-number-grid">
              <div><span className="setting-icon"><Search size={18} /></span><div><strong>结果条数</strong><small>每家公司检索返回上限</small></div><NumberStepper value={search.maxResults || 8} onChange={(value) => updateSearch({ maxResults: value })} min={1} max={20} suffix="条" /></div>
              <div><span className="setting-icon"><FileJson2 size={18} /></span><div><strong>单条摘录</strong><small>交给模型阅读的字符上限</small></div><NumberStepper value={search.maxCharsPerResult || 1500} onChange={(value) => updateSearch({ maxCharsPerResult: value })} min={200} max={6000} step={100} suffix="字符" /></div>
            </div>
            <div className="langfuse-actions">
              <span className="search-health">{!search.lastCheckedAt ? '尚未检查凭据' : search.lastCheckStatus === 'ok' ? '上次检查：正常' : '上次检查：失败'}</span>
              <div className="langfuse-command-group"><Button variant="primary" icon={Save} onClick={saveSearch} loading={busy === 'search-save'}>保存搜索服务</Button><Button icon={Activity} onClick={testSearch} loading={busy === 'search-test'} disabled={!search.hasApiKey}>检查凭据</Button></div>
            </div>
            {searchFeedback && <Callout tone={searchFeedback.tone} title={searchFeedback.title}>{searchFeedback.message}</Callout>}
            {!search.enabled && search.hasApiKey && <Callout tone="warning" title="API Key 已保存，但开关还没打开">搜索服务当前未启用，公司背景核验仍走模型自带的联网搜索。把右上角开关打开并保存后才会改用该服务。</Callout>}
            {!search.hasApiKey && <Callout tone="info" title="当前使用模型自带联网搜索">填写 API Key 并启用后，公司背景核验会先由搜索服务取回真实网页摘录，再交给模型归纳，引用只能来自这批结果。</Callout>}
            {search.configured && <Callout tone="success" title="公司背景核验已改用搜索服务">模型只阅读检索到的网页摘录并归纳，引用无法超出这批结果。「公司背景核验模型」角色仍在使用，但它现在负责归纳，不需要自带联网能力。</Callout>}
          </section>

          <section className="panel langfuse-section">
            <SectionHeader title="Langfuse" description="AI Run Trace、Prompt 版本、成本与质量评估" action={<label className="switch-control"><input type="checkbox" checked={Boolean(langfuse.enabled)} onChange={(event) => updateLangfuse({ enabled: event.target.checked })} /><span />{langfuse.enabled ? '启用' : '停用'}</label>} />
            <div className="langfuse-fields">
              <label><span>Base URL</span><input value={langfuse.baseUrl || ''} onChange={(event) => updateLangfuse({ baseUrl: event.target.value })} placeholder="https://cloud.langfuse.com" /></label>
              <label><span>Environment</span><input value={langfuse.environment || 'production'} onChange={(event) => updateLangfuse({ environment: event.target.value })} /></label>
              <label><span>Public Key</span><input value={langfuse.publicKey || ''} onChange={(event) => updateLangfuse({ publicKey: event.target.value })} placeholder="pk-lf-..." /></label>
              <label><span>Secret Key</span><div className="api-key-input"><KeyRound size={16} /><input type="password" autoComplete="new-password" value={langfuseSecret} onChange={(event) => { setLangfuseSecret(event.target.value); setLangfuseDirty(true); setLangfuseFeedback(null); }} placeholder={langfuse.hasSecretKey ? `已保存 ${langfuse.secretKeyHint}` : 'sk-lf-...'} />{langfuse.hasSecretKey && <Badge tone="success">已配置</Badge>}</div></label>
            </div>
            <div className="langfuse-actions"><label><input type="checkbox" checked={Boolean(langfuse.captureContent)} onChange={(event) => updateLangfuse({ captureContent: event.target.checked })} />记录完整模型输入输出</label><div className="langfuse-command-group"><Button variant="primary" icon={Save} onClick={saveLangfuse} loading={busy === 'langfuse-save'}>保存 Langfuse</Button><Button icon={Activity} onClick={testLangfuse} loading={busy === 'langfuse-test'}>发送测试 Trace</Button></div></div>
            {langfuseFeedback && <Callout tone={langfuseFeedback.tone} title={langfuseFeedback.title}>{langfuseFeedback.message}</Callout>}
            {langfuse.captureContent && <Callout tone="warning" title="候选人数据将进入 Langfuse">仅在已确认部署区域、访问控制与数据保留策略后启用完整内容；默认 Trace 只发送来源 ID、Hash 和统计信息。</Callout>}
          </section>
        </div>

        <aside className="models-side">
          <section className="system-readiness"><span className="side-panel-icon"><ShieldCheck size={20} /></span><h3>处理能力</h3><div className="readiness-score"><strong>{toolRows.filter(([, key]) => tools[key]).length}/{toolRows.length}</strong><span>本机工具可用</span></div><div className="readiness-list">{toolRows.map(([label, key]) => <div key={key}><span>{tools[key] ? <Check size={14} /> : <LoaderCircle size={14} />}{label}</span><Badge tone={tools[key] ? 'success' : 'danger'}>{tools[key] ? '可用' : '缺失'}</Badge></div>)}</div></section>
          <section className="context-flow-panel"><Workflow size={20} /><h3>上下文边界</h3><ol><li><i>1</i><span>组织与岗位版本</span></li><li><i>2</i><span>功能级 Context Pack</span></li><li><i>3</i><span>AI Run 独立快照</span></li><li><i>4</i><span>结构化结果晋升</span></li></ol></section>
          <section className="langfuse-status"><Database size={18} /><div><strong>Langfuse Trace</strong><span>{langfuseDirty ? '有未保存修改' : langfuse.enabled ? '已启用' : '未启用'}</span></div><Badge tone={langfuseDirty ? 'warning' : langfuse.enabled ? 'success' : 'neutral'}>{langfuseDirty ? '待保存' : langfuse.captureContent ? '完整内容' : '脱敏'}</Badge></section>
          <section className="data-location"><ServerCog size={18} /><div><strong>运行时数据</strong><code>{state?.system?.dataLocation}</code></div></section>
        </aside>
      </div>

      <Modal open={providerModal} width="680px" title="新增 Custom API Provider" description="配置一个 OpenAI Chat Completions 兼容端点" onClose={() => setProviderModal(false)} footer={<><Button variant="ghost" onClick={() => setProviderModal(false)}>取消</Button><Button variant="primary" icon={Plus} loading={busy === 'provider-create'} onClick={createProvider}>创建 Provider</Button></>}>
        <div className="modal-form provider-create-form">
          <label><span>Provider 名称</span><div className="input-with-icon"><ServerCog size={16} /><input value={newProvider.name} onChange={(event) => setNewProvider((current) => ({ ...current, name: event.target.value }))} placeholder="例如：Internal OpenAI Gateway" /></div></label>
          <label><span>API Base URL</span><div className="input-with-icon"><Network size={16} /><input value={newProvider.baseUrl} onChange={(event) => setNewProvider((current) => ({ ...current, baseUrl: event.target.value }))} placeholder="https://api.example.com/v1" /></div></label>
          <label className="modal-form-full"><span>API Key</span><div className="api-key-input"><KeyRound size={16} /><input type="password" autoComplete="new-password" value={newProvider.apiKey} onChange={(event) => setNewProvider((current) => ({ ...current, apiKey: event.target.value }))} placeholder="输入 Provider API Key" /></div></label>
          <div className="modal-section-heading"><span><FileJson2 size={16} /></span><div><strong>初始模型</strong><small>创建后仍可同步或继续添加模型</small></div></div>
          <ModelFields value={newProvider.model} onChange={(model) => setNewProvider((current) => ({ ...current, model }))} />
        </div>
      </Modal>

      <Modal open={Boolean(modelModal)} title={modelModal?.model ? '编辑模型' : '添加模型'} description={selectedProvider?.name} onClose={() => setModelModal(null)} footer={<><Button variant="ghost" onClick={() => setModelModal(null)}>取消</Button><Button variant="primary" icon={Save} loading={busy === 'model-save'} onClick={saveModel}>保存模型</Button></>}><ModelFields value={modelDraft} onChange={setModelDraft} /></Modal>
    </div>
  );
}
